import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, TrendingUp, Users, Activity, ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "../lib/api";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/donations/stats"),
        api.get("/donations/list"),
      ]);
      setStats(s.data);
      setDonations(l.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const statusColor = (s) => {
    if (s === "finished") return "bg-olive-light text-olive";
    if (s === "failed" || s === "expired") return "bg-red-100 text-red-700";
    return "bg-bone text-ink-muted";
  };

  return (
    <div className="min-h-screen bg-alabaster" data-testid="admin-page">
      <header className="border-b border-stone-border/60 bg-alabaster/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-ink-muted hover:text-terracotta transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Heart className="w-5 h-5 text-terracotta fill-terracotta" />
            <span className="font-heading text-2xl font-semibold text-ink">Admin Dashboard</span>
          </div>
          <button
            onClick={load}
            data-testid="refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-border hover:border-terracotta text-ink-body hover:text-terracotta text-sm transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-5 mb-12">
          {[
            { icon: TrendingUp, label: "Total Raised", value: stats ? `$${stats.total_raised.toFixed(2)}` : "—", color: "text-terracotta", bg: "bg-terracotta-light" },
            { icon: Users, label: "Total Donors", value: stats?.donor_count ?? "—", color: "text-olive", bg: "bg-olive-light" },
            { icon: Activity, label: "Avg. Donation", value: stats ? `$${stats.average_donation.toFixed(2)}` : "—", color: "text-ink", bg: "bg-bone" },
            { icon: Heart, label: "This Month", value: stats ? `$${stats.monthly_raised.toFixed(2)}` : "—", color: "text-terracotta", bg: "bg-terracotta-light" },
          ].map((card, i) => (
            <div key={i} data-testid={`stat-card-${i}`} className="bg-white rounded-2xl p-6 border border-stone-border/60 shadow-soft">
              <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center mb-4`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-medium">{card.label}</div>
              <div className="font-heading text-3xl font-semibold text-ink mt-2">{card.value}</div>
            </div>
          ))}
        </div>

        {/* Donations Table */}
        <div className="bg-white rounded-3xl border border-stone-border/60 shadow-soft overflow-hidden">
          <div className="px-8 py-6 border-b border-stone-border/60">
            <h2 className="font-heading text-2xl font-semibold text-ink">Recent Donations</h2>
            <p className="text-sm text-ink-muted mt-1">{donations.length} total donations</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="donations-table">
              <thead className="bg-bone">
                <tr className="text-left text-xs uppercase tracking-wider text-ink-muted">
                  <th className="px-8 py-4 font-medium">Date</th>
                  <th className="px-4 py-4 font-medium">Donor</th>
                  <th className="px-4 py-4 font-medium">Amount</th>
                  <th className="px-4 py-4 font-medium">Message</th>
                  <th className="px-4 py-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-border/50">
                {donations.length === 0 && !loading && (
                  <tr>
                    <td colSpan={5} className="px-8 py-16 text-center text-ink-muted">No donations yet. Share your donation link to get started.</td>
                  </tr>
                )}
                {donations.map((d, i) => (
                  <tr key={d.id} data-testid={`donation-row-${i}`} className="hover:bg-bone/50 transition-colors">
                    <td className="px-8 py-4 text-sm text-ink-body whitespace-nowrap">
                      {new Date(d.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-ink">{d.donor_name || "Anonymous"}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-ink">${Number(d.amount).toFixed(2)}</td>
                    <td className="px-4 py-4 text-sm text-ink-body max-w-xs truncate">{d.message || <span className="text-ink-muted">—</span>}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium capitalize ${statusColor(d.status)}`}>
                        {d.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
